import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import React from "react";
import { useForm, Controller } from "react-hook-form";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    TextField,
    Stack,
    FormControlLabel,
    Switch,
    Typography,
    Box,
    MenuItem,
    Tabs,
    Tab,
    Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CustomImage from "../../components/CustomImage";
import ImageUpload from "../../components/ImageUploader";
import { getUserCorporateId, isHRUser, omitEmptyOptionalFields, uploadFile } from "../../utils/helper";
import { MODULES } from "../../utils/constants";
import _ from "lodash";
import { showError } from "../../services/toaster";
import { useUserStore } from "../../services/user";
import { UserBulkUploadResult, UserData } from "../../types/user";
import FieldSearch, { FieldSearchConfig } from "../../components/FieldSearch";
import { doGET } from "../../utils/HttpUtils";
import { useCorporateStore } from "../../services/corporates";
import { useAppContext } from "../../services/context/AppContext";
import DeletedUsers from './DeletedUsers';

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & { children: React.ReactElement<any, any> },
    ref: React.Ref<unknown>
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`user-tabpanel-${index}`}
            aria-labelledby={`user-tab-${index}`}
            {...other}
            style={{ paddingTop: '20px' }}
        >
            {value === index && (
                <Box>
                    {children}
                </Box>
            )}
        </div>
    );
}

const userSearchFields: FieldSearchConfig[] = [
    {
        fieldName: "all",
        label: "All",
        inputType: "text",
        placeholder: "Search by mobile, email, name, or status",
    },
    {
        fieldName: "phone",
        label: "Mobile Number",
        inputType: "tel",
        placeholder: "Enter mobile number",
    },
    {
        fieldName: "email",
        label: "Email ID",
        inputType: "email",
        placeholder: "Enter email ID",
    },
    {
        fieldName: "name",
        label: "Name",
        inputType: "text",
        placeholder: "Enter name",
    },
];

const AddUserDialog = ({ isModalOpen, toggleModal, selectedId, hideAddButton = false, showDeletedUsers = false }: any) => {
    const { onCreate, onUpdate, detail, fetchGrid, filters, setFilters, exportUsers, bulkUploadUsers } = useUserStore();
    const { allData: corporateData, fetchAll: fetchAllCorporates } = useCorporateStore();
    const { userData } = useAppContext();
    const isHR = isHRUser(userData);
    const hrCorporateId = getUserCorporateId(userData);
    const [tabValue, setTabValue] = React.useState(0);
    const [imageFile, setImageFile] = React.useState<File | null>(null);
    const [previewImage, setPreviewImage] = React.useState<string>("");
    const [walletDetails, setWalletDetails] = React.useState<any>(null);
    const [userTransactions, setUserTransactions] = React.useState<any[]>([]);
    const [couponUsage, setCouponUsage] = React.useState<any[]>([]);
    const [isExporting, setIsExporting] = React.useState(false);
    const [bulkDialogOpen, setBulkDialogOpen] = React.useState(false);
    const [bulkFile, setBulkFile] = React.useState<File | null>(null);
    const [bulkCorporateId, setBulkCorporateId] = React.useState("");
    const [bulkUploading, setBulkUploading] = React.useState(false);
    const [bulkResult, setBulkResult] = React.useState<UserBulkUploadResult | null>(null);

    const handleUserSearch = (searchValues: Record<string, string>) => {
        const newFilters: any = { ...filters };

        userSearchFields.forEach((field) => {
            delete newFilters[field.fieldName];
        });
        delete newFilters.isActive;
        delete newFilters.search;

        if (searchValues.all) {
            const searchTerm = searchValues.all.trim();
            const normalizedSearchTerm = searchTerm.toLowerCase();

            if (normalizedSearchTerm === "active") {
                newFilters.isActive = "true";
            } else if (normalizedSearchTerm === "inactive") {
                newFilters.isActive = "false";
            } else if (/^\d{5,}$/.test(searchTerm)) {
                newFilters.phone = searchTerm;
            } else if (searchTerm.includes("@")) {
                newFilters.email = searchTerm;
            } else {
                newFilters.name = searchTerm;
            }
        } else {
            Object.assign(newFilters, searchValues);
        }

        setFilters(newFilters);
    };

    const { register, handleSubmit, control, setValue, watch, reset, formState: { errors } } = useForm<UserData>({
        defaultValues: {
            userType: 'user',
            isActive: true,
            isVerified: false,
            gender: 'Male',
        }
    });

    const formData = watch();

    React.useEffect(() => {
        fetchAllCorporates();
    }, [fetchAllCorporates]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handleClose = () => {
        toggleModal(false);
        reset();
        setImageFile(null);
        setPreviewImage("");
        setWalletDetails(null);
        setUserTransactions([]);
        setCouponUsage([]);
        setTabValue(0);
    };

    const fetchWallet = async (id: string) => {
        try {
            const res = await doGET(`/wallet/user/${id}`);
            if (res?.data?.data) {
                setWalletDetails(res.data.data);
            } else {
                setWalletDetails(null);
            }
        } catch (error) {
            console.error("Error fetching wallet details:", error);
            setWalletDetails(null);
        }
    };

    const fetchTransactions = async (id: string) => {
        try {
            const res = await doGET(`/wallet/user/${id}/transactions`);
            if (res?.data?.data) {
                setUserTransactions(res.data.data);
            } else {
                setUserTransactions([]);
            }
        } catch (error) {
            console.error("Error fetching transactions:", error);
            setUserTransactions([]);
        }
    };

    const fetchCouponUsage = async (id: string) => {
        try {
            const res = await doGET(`/coupons/usage/user/${id}?page=1&limit=100`);
            const coupons = res?.data?.data?.coupons;

            if (Array.isArray(coupons)) {
                setCouponUsage(coupons);
            } else {
                setCouponUsage([]);
            }
        } catch (error) {
            console.error("Error fetching coupon usage:", error);
            setCouponUsage([]);
        }
    };

    const onSubmit = async (data: UserData) => {
        try {
            let response: UserData | null = null;
            const payload = omitEmptyOptionalFields({ ...data });

            if (selectedId && data.corporateId === null) {
                payload.corporateId = null;
            }

            if (selectedId) {
                payload._id = selectedId;
                response = await onUpdate(payload);
            } else {
                response = await onCreate(payload);
            }

            if (response?._id && imageFile) {
                const res = await uploadFile(
                    { module: MODULES.USER, record_id: response._id },
                    [imageFile]
                );
                if (res.status >= 200 && res.status < 400) {
                    const imagePath = res.data?.data?.length ? res.data.data[0] : "";
                    await onUpdate({ _id: response._id, profilePictureUrl: imagePath });
                }
            }

            handleClose();
            fetchGrid();
        } catch (error) {
            console.error("Error submitting user:", error);
            showError("An error occurred while saving the user");
        }
    };

    const handleExportUsers = async () => {
        setIsExporting(true);
        try {
            await exportUsers();
        } finally {
            setIsExporting(false);
        }
    };

    const handleBulkDialogClose = () => {
        if (bulkUploading) return;
        setBulkDialogOpen(false);
        setBulkFile(null);
        setBulkCorporateId("");
        setBulkResult(null);
    };

    const handleBulkUpload = async () => {
        if (!bulkFile) {
            showError("Select a CSV, XLS, or XLSX file");
            return;
        }

        setBulkUploading(true);
        try {
            const result = await bulkUploadUsers({
                file: bulkFile,
                corporateId: isHR ? hrCorporateId : bulkCorporateId || undefined,
            });
            setBulkResult(result);
        } finally {
            setBulkUploading(false);
        }
    };

    const fetchDetail = async (id: string) => {
        try {
            const res: any = await detail(id);
            // API returns: { data: { user: { ... } } }
            const userData = res?.data?.user || res?.data || res;
            if (userData && userData._id) {
                reset(userData);
                if (userData.profilePictureUrl) {
                    setPreviewImage(userData.profilePictureUrl);
                }
            }
        } catch (error) {
            console.error("Error fetching user detail:", error);
            showError("Failed to load user details");
        }
    };

    React.useEffect(() => {
        if (isModalOpen) {
            if (selectedId) {
                fetchDetail(selectedId);
                fetchWallet(selectedId);
                fetchTransactions(selectedId);
                fetchCouponUsage(selectedId);
            } else {
                reset({
                    userType: 'user',
                    isActive: true,
                    isVerified: false,
                    gender: 'Male',
                    name: "",
                    email: "",
                    phone: "",
                    address: "",
                    city: "",
                    state: "",
                    country: "India",
                    corporateId: isHR ? hrCorporateId : undefined,
                });
                setImageFile(null);
                setPreviewImage("");
                setWalletDetails(null);
                setUserTransactions([]);
                setCouponUsage([]);
            }
        }
    }, [selectedId, isModalOpen]);

    return (
        <>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2}>
                <FieldSearch
                    fields={userSearchFields}
                    defaultFieldName="all"
                    onSearch={handleUserSearch}
                />
                <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                    <Button
                        variant="outlined"
                        startIcon={<FileDownloadOutlinedIcon />}
                        onClick={handleExportUsers}
                        disabled={isExporting}
                    >
                        {isExporting ? "Exporting..." : "Export Users"}
                    </Button>
                    {!hideAddButton && (
                        <Button
                            variant="outlined"
                            startIcon={<UploadFileIcon />}
                            onClick={() => setBulkDialogOpen(true)}
                        >
                            Bulk Upload
                        </Button>
                    )}
                    {!hideAddButton && (
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() => toggleModal(true)}
                        >
                            Add User
                        </Button>
                    )}
                    {showDeletedUsers && <DeletedUsers />}
                </Stack>
            </Stack>

            <Dialog
                open={bulkDialogOpen}
                onClose={handleBulkDialogClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Typography variant="h6" fontWeight="600">
                        Bulk Upload Users
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2.5}>
                        {!isHR && (
                            <TextField
                                select
                                label="Corporate"
                                value={bulkCorporateId}
                                onChange={(event) => setBulkCorporateId(event.target.value)}
                                fullWidth
                                helperText="Optional. Leave empty for general users."
                            >
                                <MenuItem value=""><em>General users</em></MenuItem>
                                {corporateData.map((corporate: any) => (
                                    <MenuItem key={corporate._id} value={corporate._id}>
                                        {corporate.companyName || corporate.name || corporate.email || corporate._id}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}
                        <Button
                            component="a"
                            href={`${process.env.PUBLIC_URL || ""}/sample-user-bulk-upload-template.xlsx`}
                            download="sample-user-bulk-upload-template.xlsx"
                            variant="outlined"
                            startIcon={<FileDownloadOutlinedIcon />}
                        >
                            Download Sample file
                        </Button>
                        <Button
                            component="label"
                            variant="outlined"
                            startIcon={<UploadFileIcon />}
                        >
                            {bulkFile ? bulkFile.name : "Select CSV, XLS, or XLSX"}
                            <input
                                hidden
                                type="file"
                                accept=".csv,.xls,.xlsx"
                                onChange={(event) => {
                                    const file = event.target.files?.[0] || null;
                                    setBulkFile(file);
                                    setBulkResult(null);
                                }}
                            />
                        </Button>
                        <Typography variant="body2" color="text.secondary">
                            Supported headers include: Sl. No., Emp Id / Infinity Id, Name, DOB, Gender, Mobile Number, Email Id, corporateEmail, city, state, pincode, country.
                        </Typography>
                        {bulkResult && (
                            <Box sx={{ p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
                                <Typography variant="subtitle2">
                                    Created: {bulkResult.created.length} | Skipped: {bulkResult.skipped.length} | Failed: {bulkResult.failed.length}
                                </Typography>
                                {!!bulkResult.failed.length && (
                                    <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                                        First failed row {bulkResult.failed[0].rowNumber}: {bulkResult.failed[0].reason}
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleBulkDialogClose} disabled={bulkUploading}>
                        Close
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleBulkUpload}
                        disabled={!bulkFile || bulkUploading}
                    >
                        {bulkUploading ? "Uploading..." : "Upload Users"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={isModalOpen}
                onClose={handleClose}
                TransitionComponent={Transition}
                fullScreen
            >
                <form onSubmit={handleSubmit(onSubmit)} style={{ height: "100%", display: "flex", flexDirection: "column" }}>
                    <DialogTitle>
                        <Typography variant="h5" fontWeight="600">
                            {selectedId ? "Edit User Account" : "Create New User Account"}
                        </Typography>
                    </DialogTitle>

                    <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
                        <Tabs value={tabValue} onChange={handleTabChange} aria-label="user details tabs">
                            <Tab label="Basic Info" />
                            <Tab label="Profile Details" />
                            <Tab label="Address & Location" />
                            <Tab label="Access & Professional" />
                            <Tab label="User Transaction Details" />
                        </Tabs>
                    </Box>

                    <DialogContent dividers style={{ flex: 1, overflowY: "auto", padding: '24px' }}>
                        <CustomTabPanel value={tabValue} index={0}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={4}>
                                    <Stack spacing={2} alignItems="center">
                                        <Box sx={{ width: 200, height: 200, borderRadius: '50%', border: '4px solid #f0f0f0', overflow: 'hidden', position: 'relative', bgcolor: '#fafafa', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                            {previewImage ? (
                                                <CustomImage src={previewImage} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : (
                                                <Typography variant="caption" color="textSecondary">No Image</Typography>
                                            )}
                                        </Box>
                                        <ImageUpload
                                            onChange={(files: any) => {
                                                if (files?.length) {
                                                    setImageFile(files[0]);
                                                    setPreviewImage(URL.createObjectURL(files[0]));
                                                }
                                            }}
                                            allow="image/*"
                                            multiple={false}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} md={8}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12}>
                                            <TextField
                                                label="Full Name"
                                                fullWidth
                                                {...register("name", { required: "Name is required" })}
                                                error={!!errors.name}
                                                helperText={errors.name?.message}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                label="Email Address"
                                                fullWidth
                                                {...register("email", { pattern: { value: /^\S+@\S+$/i, message: "Invalid email" } })}
                                                error={!!errors.email}
                                                helperText={errors.email?.message}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                label="Phone Number"
                                                fullWidth
                                                {...register("phone")}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Controller
                                                name="userType"
                                                control={control}
                                                rules={{ required: "User type is required" }}
                                                render={({ field }) => (
                                                    <TextField
                                                        select
                                                        label="User Type"
                                                        fullWidth
                                                        value={field.value || 'user'}
                                                        onChange={field.onChange}
                                                        onBlur={field.onBlur}
                                                        inputRef={field.ref}
                                                    >
                                                        <MenuItem value="user">Standard User</MenuItem>
                                                        <MenuItem value="admin">Administrator</MenuItem>
                                                    </TextField>
                                                )}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Stack direction="row" spacing={4} sx={{ mt: 1 }}>
                                                <FormControlLabel
                                                    control={<Controller name="isActive" control={control} render={({ field }) => (
                                                        <Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
                                                    )} />}
                                                    label="Active Account"
                                                />
                                                <FormControlLabel
                                                    control={<Controller name="isVerified" control={control} render={({ field }) => (
                                                        <Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
                                                    )} />}
                                                    label="Verified User"
                                                />
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </CustomTabPanel>

                        <CustomTabPanel value={tabValue} index={1}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        label="Date of Birth (YYYYMMDD)"
                                        fullWidth
                                        {...register("dateOfBirth")}
                                        placeholder="e.g. 19900101"
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Controller
                                        name="gender"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                select
                                                label="Gender"
                                                fullWidth
                                                value={field.value || 'Male'}
                                                onChange={field.onChange}
                                                onBlur={field.onBlur}
                                                inputRef={field.ref}
                                            >
                                                <MenuItem value="Male">Male</MenuItem>
                                                <MenuItem value="Female">Female</MenuItem>
                                                <MenuItem value="Other">Other</MenuItem>
                                            </TextField>
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Controller
                                        name="bloodGroup"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                select
                                                label="Blood Group"
                                                fullWidth
                                                value={field.value || ''}
                                                onChange={field.onChange}
                                                onBlur={field.onBlur}
                                                inputRef={field.ref}
                                            >
                                                <MenuItem value=""><em>None</em></MenuItem>
                                                {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(group => (
                                                    <MenuItem key={group} value={group}>{group}</MenuItem>
                                                ))}
                                            </TextField>
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        label="Height (cm)"
                                        type="number"
                                        fullWidth
                                        {...register("height", { valueAsNumber: true })}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        label="Weight (kg)"
                                        type="number"
                                        fullWidth
                                        {...register("weight", { valueAsNumber: true })}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 1 }}><Typography variant="caption" color="textSecondary">Emergency Contact</Typography></Divider>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        label="EC Name"
                                        fullWidth
                                        {...register("emergencyContact.name")}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        label="EC Phone"
                                        fullWidth
                                        {...register("emergencyContact.phone")}
                                    />
                                </Grid>
                                {/* <Grid item xs={12} md={4}>
                                    <TextField
                                        label="Relationship"
                                        fullWidth
                                        {...register("emergencyContact.relationship")}
                                    />
                                </Grid> */}
                            </Grid>
                        </CustomTabPanel>

                        <CustomTabPanel value={tabValue} index={2}>
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <TextField
                                        label="Street Address"
                                        fullWidth
                                        multiline
                                        rows={2}
                                        {...register("address")}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        label="City"
                                        fullWidth
                                        {...register("city")}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        label="State"
                                        fullWidth
                                        {...register("state")}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        label="Pincode"
                                        fullWidth
                                        {...register("pincode")}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        label="Country"
                                        fullWidth
                                        {...register("country")}
                                    />
                                </Grid>
                            </Grid>
                        </CustomTabPanel>

                        <CustomTabPanel value={tabValue} index={3}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Controller
                                        name="corporateId"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                select
                                                label="Corporate"
                                                fullWidth
                                                value={field.value ?? ""}
                                                onChange={(event) => {
                                                    const nextValue = event.target.value;
                                                    field.onChange(nextValue || null);
                                                }}
                                                helperText={isHR ? "Users stay locked to your corporate" : "Choose a corporate or leave it as None"}
                                                disabled={isHR}
                                            >
                                                {!isHR && <MenuItem value="">None</MenuItem>}
                                                {corporateData.map((corporate: any) => (
                                                    <MenuItem key={corporate._id} value={corporate._id}>
                                                        {corporate.name}
                                                        {corporate.companyName ? ` — ${corporate.companyName}` : ""}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        label="Department"
                                        fullWidth
                                        {...register("department")}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        label="Employee ID"
                                        fullWidth
                                        {...register("employeeId")}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        label="Permissions (Comma separated)"
                                        fullWidth
                                        placeholder="e.g. read:users, write:blogs"
                                        onChange={(e) => {
                                            const values = e.target.value.split(',').map(v => v.trim());
                                            setValue('permissions', values);
                                        }}
                                        defaultValue={formData.permissions?.join(', ')}
                                    />
                                </Grid>
                            </Grid>
                        </CustomTabPanel>
                        <CustomTabPanel value={tabValue} index={4}>
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold' }}>Wallet Details</Typography>
                                </Grid>
                                {walletDetails ? (
                                    <>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                label="Wallet Balance"
                                                fullWidth
                                                value={walletDetails.balance ?? ''}
                                                disabled
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                label="Wallet Active"
                                                fullWidth
                                                value={walletDetails.isActive ? 'Yes' : 'No'}
                                                disabled
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                label="Created At"
                                                fullWidth
                                                value={walletDetails.createdAt ? new Date(walletDetails.createdAt).toLocaleString() : ''}
                                                disabled
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                label="Updated At"
                                                fullWidth
                                                value={walletDetails.updatedAt ? new Date(walletDetails.updatedAt).toLocaleString() : ''}
                                                disabled
                                            />
                                        </Grid>
                                    </>
                                ) : (
                                    <Grid item xs={12}>
                                        <Typography color="textSecondary">{selectedId ? "No wallet details found for this user." : "Wallet details will be available after the user is created."}</Typography>
                                    </Grid>
                                )}
                                {selectedId && (
                                    <>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold', mt: 2 }}>Coupon Used</Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            {couponUsage.length > 0 ? (
                                                <Box sx={{ border: '1px solid #eee', borderRadius: 2, overflow: 'hidden' }}>
                                                    {couponUsage.map((coupon: any, index: number) => (
                                                        <Box
                                                            key={`${coupon.couponCode}-${index}`}
                                                            sx={{
                                                                p: 2,
                                                                borderBottom: index < couponUsage.length - 1 ? '1px solid #eee' : 'none'
                                                            }}
                                                        >
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                                                                <Box>
                                                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                                        {coupon.couponCode || 'N/A'}
                                                                    </Typography>
                                                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                                        Used {coupon.usageCount || 0} time(s)
                                                                    </Typography>
                                                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                                        Context: {Array.isArray(coupon.contexts) && coupon.contexts.length ? coupon.contexts.join(', ') : 'N/A'}
                                                                    </Typography>
                                                                </Box>
                                                                <Box textAlign="right">
                                                                    <Typography variant="h6" fontWeight="bold" color="success.main">
                                                                        {typeof coupon.totalDiscountAmount === 'number' ? `₹${coupon.totalDiscountAmount}` : '-'}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                                                                        First used: {coupon.firstUsedAt ? new Date(coupon.firstUsedAt).toLocaleString() : 'N/A'}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                                                                        Last used: {coupon.lastUsedAt ? new Date(coupon.lastUsedAt).toLocaleString() : 'N/A'}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            ) : (
                                                <Typography color="textSecondary">
                                                    No coupon usage found.
                                                </Typography>
                                            )}
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold', mt: 2 }}>Transaction History</Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            {userTransactions && userTransactions.length > 0 ? (
                                                <Box sx={{ border: '1px solid #eee', borderRadius: 2, overflow: 'hidden' }}>
                                                    {userTransactions.map((tx: any, index: number) => (
                                                        <Box key={tx._id || index} sx={{ p: 2, borderBottom: index < userTransactions.length - 1 ? '1px solid #eee' : 'none' }}>
                                                            <Box sx={{ mb: 1.5 }}>
                                                                <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary', display: 'flex', gap: 1 }}>
                                                                    <span>Description:</span> 
                                                                    <span style={{ fontWeight: 400 }}>{tx.description || 'N/A'}</span>
                                                                </Typography>
                                                                {tx.notes && (
                                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', display: 'flex', gap: 1, mt: 0.5 }}>
                                                                        <span>Notes:</span> 
                                                                        <span style={{ fontWeight: 400 }}>{tx.notes}</span>
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#fafafa', p: 1.5, borderRadius: 1 }}>
                                                                <Box>
                                                                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                                                                        <strong>Status:</strong> <span style={{ textTransform: 'capitalize' }}>{tx.status}</span>
                                                                    </Typography>
                                                                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                                                                        <strong>Date:</strong> {tx.transactionDate ? tx.transactionDate.replace(/(\d{4})(\d{2})(\d{2})/, "$1-$2-$3") : new Date(tx.createdAt).toLocaleDateString()}
                                                                    </Typography>
                                                                </Box>
                                                                <Box textAlign="right">
                                                                    <Typography variant="h6" fontWeight="bold" color={tx.transactionType === 'credit' ? 'success.main' : 'error.main'}>
                                                                        {tx.transactionType === 'credit' ? '+' : '-'}{tx.amount}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            ) : (
                                                <Typography color="textSecondary">No transactions found.</Typography>
                                            )}
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                        </CustomTabPanel>
                    </DialogContent>

                    <DialogActions sx={{ p: 3, bgcolor: '#fbfbfb' }}>
                        <Button onClick={handleClose} variant="outlined" sx={{ minWidth: 120 }}>Cancel</Button>
                        <Box sx={{ flex: 1 }} />
                        {tabValue > 0 && (
                            <Button onClick={() => setTabValue(tabValue - 1)} variant="text" sx={{ mr: 1 }}>Previous Step</Button>
                        )}
                        {tabValue < 4 ? (
                            <Button onClick={() => setTabValue(tabValue + 1)} variant="contained" color="primary" sx={{ minWidth: 120 }}>Next Step</Button>
                        ) : (
                            <Button type="submit" variant="contained" color="success" sx={{ minWidth: 120 }}>
                                {selectedId ? "Update User" : "Create User"}
                            </Button>
                        )}
                    </DialogActions>
                </form>
            </Dialog>
        </>
    );
};

export default AddUserDialog;
